import {useInfiniteScroll} from '@continuum/ui/hooks';
import {useEffect, useId, useRef, useState} from 'react';
import {PageRow} from './page-row';
import {
  PagesListEmpty,
  PagesListError,
  PagesListLoadMoreError,
  PagesListSkeleton,
} from './pages-list-states';
import type {PageSection} from './utils';

interface Props {
  sections: PageSection[];
  showAdvisor: boolean;
  currentIdentifier: string;
  loading: boolean;
  error: boolean;
  hasNextPage: boolean;
  isFetchingMore: boolean;
  loadMoreFailed: boolean;
  onLoadMore: () => void;
  onRetry: () => void;
  onNavigate: () => void;
}

export const PagesList = ({
  sections,
  showAdvisor,
  currentIdentifier,
  loading,
  error,
  hasNextPage,
  isFetchingMore,
  loadMoreFailed,
  onLoadMore,
  onRetry,
  onNavigate,
}: Props) => {
  const headingId = useId();
  const [scrollRoot, setScrollRoot] = useState<HTMLDivElement | null>(null);
  // A failed fetch stops the sentinel, or scrolling would retry it on loop.
  const loadMoreTriggerRef = useInfiniteScroll({
    onLoadMore,
    hasNextPage,
    isLoading: isFetchingMore,
    enabled: !loadMoreFailed,
    root: scrollRoot,
  });

  // No dependency array: the row does not exist on the first render, so this
  // re-checks until it does; the ref guard makes every later run a no-op.
  const currentRowRef = useRef<HTMLElement | null>(null);
  const didScrollToCurrent = useRef(false);
  useEffect(() => {
    if (didScrollToCurrent.current || !currentRowRef.current) {
      return;
    }
    didScrollToCurrent.current = true;
    currentRowRef.current.scrollIntoView({block: 'nearest'});
  });

  if (loading) {
    return (
      <div className="p-3">
        <PagesListSkeleton count={5} />
      </div>
    );
  }

  if (error) {
    return <PagesListError onRetry={onRetry} />;
  }

  const itemCount = sections.reduce(
    (sum, section) => sum + section.items.length,
    0,
  );

  if (itemCount === 0) {
    return <PagesListEmpty />;
  }

  return (
    <div
      ref={setScrollRoot}
      className="scrollbar-overlay h-full overflow-y-auto px-3 pb-3"
    >
      {sections.map((section) => {
        const labelId = `${headingId}-${section.key}`;
        return (
          <section
            key={section.key}
            aria-labelledby={section.label ? labelId : undefined}
          >
            {section.label ? (
              <h3
                id={labelId}
                className="sticky top-0 z-10 bg-background px-1 pb-2 pt-4 text-[11px] font-medium uppercase tracking-widest text-muted-foreground"
              >
                {section.label}
              </h3>
            ) : (
              <div className="h-3" />
            )}
            <ul className="space-y-2">
              {section.items.map((item) => {
                const isCurrent = item.identifier === currentIdentifier;
                return (
                  <li key={item.identifier}>
                    <PageRow
                      ref={isCurrent ? currentRowRef : undefined}
                      item={item}
                      isCurrent={isCurrent}
                      showAdvisor={showAdvisor}
                      onNavigate={onNavigate}
                    />
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}
      {itemCount === 1 && !hasNextPage && (
        <p className="px-1 pt-3 text-xs text-muted-foreground">
          This is the only page that has been shared with you.
        </p>
      )}
      {hasNextPage && isFetchingMore && (
        <div className="pt-2">
          <PagesListSkeleton count={3} />
        </div>
      )}
      {hasNextPage && !isFetchingMore && loadMoreFailed && (
        <PagesListLoadMoreError onRetry={onLoadMore} />
      )}
      <div ref={loadMoreTriggerRef} aria-hidden className="h-px" />
    </div>
  );
};
